// ── THE COVERAGE-LEVEL REPLAY ────────────────────────────────────────────────
//
// "CY Farms have 60% coverage. Should they go to 65%, 75%, or stay?"  The
// recommendation engine could not answer that, because its candidate set was
// [0.75, 0.80, 0.85] and 60% is not in it.  This module answers the narrower,
// dollars-and-cents question underneath it: **on this farm's own record, what
// would each coverage level have collected, and what would it have cost?**
//
//     revenue guarantee /ac = APH(year) x level x max(projected, harvest)
//     actual revenue    /ac = actual yield x harvest price
//     indemnity         /ac = max(0, guarantee - actual revenue)
//     net               /ac = indemnity - producer premium
//
// UNDERLYING RP ONLY.  No SCO, no ECO — Kevin's scope call, and the right one:
// the area endorsements already have their own trigger history on the same page,
// and folding them in here would answer a different question with the same
// number.
//
// ── WHAT IS MEASURED AND WHAT IS NOT ─────────────────────────────────────────
//
// **THE APH RULE IS PROVEN, NOT RECALLED.**  A simple average of the ten most
// recent reported yields reproduces the AIP's own stored `rate_yield`
// EXACTLY on 74 of 74 CY2026 wheat databases that carry ten years
// (measured 2026-09-22, MAE 0.22 bu).  Agreement by depth:
//
//     10 yrs   74 of  74  (100%)   MAE 0.22 bu
//      4 yrs  150 of 177  ( 85%)   MAE 0.91
//    1-3 yrs    8 of  95  (  8%)   MAE 6-17
//
// The 1-3 year collapse is RMA's FOUR-YEAR MINIMUM: below four yields RMA fills
// the database with transitional (T) yields, which are not reported production
// and are in nothing we hold.  So a database that thin is REFUSED, never
// averaged — an APH built from one year of 100 bu against a stored 39 is not a
// small error, it is a different number.
//
// **`rate_yield` IS THE TARGET, NOT `aph_yield`.**  Measured on the same units:
// where NO yield option is elected the two columns are identical, and where TA
// or YA is elected `aph_yield` sits above `rate_yield` by the elected
// adjustment.  This module reconstructs the unadjusted average and says so; it
// does not model TA or YA, because the T-yields and substitute yields those
// options draw on are not in our data.  A farm carrying them has a HIGHER
// approved yield than this replay assumes, so the replay understates its
// guarantee — stated, and in the direction that understates indemnity.
//
// ⚠ **THE REPLAY CANNOT REACH BACK AS FAR AS THE YIELDS DO.**  For crop year Y
// the APH is built from Y-10..Y-1, and reported production starts at 2015 — so
// an as-of-2018 database holds three years and is refused by the rule above.
// The replay therefore begins in the first year the depth rule allows and the
// per-year depth rides on every row.  It is NOT padded to make the history look
// longer.
import { WHEAT_APH_MIN_YEARS, WHEAT_APH_MAX_YEARS } from "./levelLadder.js";

export const REPLAY_REFUSED = {
  THIN_DATABASE: "thin_database",     // fewer than four reported yields
  NO_YIELD: "no_yield",               // nothing reported for the year itself
  NO_PRICE: "no_price",               // RMA published no projected/harvest price we hold
  NO_PREMIUM: "no_premium",           // the rate tables cannot price this cell
};

export const REFUSAL_TEXT = {
  [REPLAY_REFUSED.THIN_DATABASE]:
    "fewer than four reported yields — RMA fills a database this thin with transitional yields we do not hold",
  [REPLAY_REFUSED.NO_YIELD]: "no yield reported for this year",
  [REPLAY_REFUSED.NO_PRICE]: "RMA's projected or harvest price for this year is not in our data",
  [REPLAY_REFUSED.NO_PREMIUM]: "the rate tables cannot price this county, practice and level",
};

const num = (v) => (v == null || v === "" || !isFinite(+v) ? null : +v);
// Money moves in integer cents.  The repo has paid twice for float dollars.
const cents = (d) => Math.round(d * 100);
const dollars = (c) => c / 100;

// ── the APH as of a crop year ────────────────────────────────────────────────
//
// `years` is {cropYear: {yield, acres, recordType}} for ONE database.  Only
// yields strictly BEFORE `asOf` can be in that year's database — the year's own
// production is not known when the guarantee is written.
export function aphAsOf(years, asOf, opts = {}) {
  const maxY = opts.maxYears || WHEAT_APH_MAX_YEARS;
  const minY = opts.minYears || WHEAT_APH_MIN_YEARS;
  const hist = Object.values(years || {})
    .filter((r) => r && +r.cropYear < asOf && num(r.yield) > 0)
    .sort((a, b) => b.cropYear - a.cropYear)
    .slice(0, maxY);
  if (hist.length < minY) {
    return { aph: null, n: hist.length, exact: false, refused: REPLAY_REFUSED.THIN_DATABASE };
  }
  const sum = hist.reduce((t, r) => t + num(r.yield), 0);
  return {
    aph: Math.round((sum / hist.length) * 10) / 10,
    n: hist.length,
    // Proven exact against the AIP's own rate_yield only at full depth.
    exact: hist.length >= maxY,
    years: hist.map((r) => r.cropYear),
    refused: null,
  };
}

// ── one (database, year, level) ──────────────────────────────────────────────
//
// `price` is {projected, harvest}.  RP's harvest-price provision raises the
// guarantee when the harvest price exceeds the projected one, which is not a
// corner case: over 2015-2025 NY wheat it happened in FIVE of eleven years.
export function replayCell({ aph, actualYield, price, level, premiumPerAc = null }) {
  const p = num(price && price.projected);
  const h = num(price && price.harvest);
  const y = num(actualYield);
  const a = num(aph);
  if (p == null || h == null) return { refused: REPLAY_REFUSED.NO_PRICE };
  if (y == null) return { refused: REPLAY_REFUSED.NO_YIELD };
  if (a == null) return { refused: REPLAY_REFUSED.THIN_DATABASE };

  const guarPrice = Math.max(p, h);
  const guarC = cents(a * level * guarPrice);
  const revC = cents(y * h);
  const indemC = Math.max(0, guarC - revC);
  const premC = premiumPerAc == null ? null : cents(premiumPerAc);
  return {
    refused: null,
    level,
    guaranteePerAc: dollars(guarC),
    revenuePerAc: dollars(revC),
    indemnityPerAc: dollars(indemC),
    premiumPerAc: premC == null ? null : dollars(premC),
    netPerAc: premC == null ? null : dollars(indemC - premC),
    triggered: indemC > 0,
    // The provision that moved the guarantee, so a reader can check it.
    guaranteePrice: guarPrice,
    harvestPriceProvision: h > p,
  };
}

// ── one database, every year, every level ────────────────────────────────────
//
// `premiumFor(level, year, aph)` returns the producer premium per acre, or null
// where the rate tables cannot price it.  It is injected rather than imported so
// the caller decides WHICH year's rates are used — and so this module can be
// driven with no rate table at all.
export function replayDatabase({ years, prices, levels, premiumFor, firstYear = null, lastYear = null }) {
  const yrs = Object.keys(years || {}).map(Number).filter((y) => isFinite(y)).sort((a, b) => a - b);
  if (!yrs.length) return { rows: [], refusals: { [REPLAY_REFUSED.NO_YIELD]: 1 } };
  const lo = firstYear == null ? yrs[0] : firstYear;
  const hi = lastYear == null ? yrs[yrs.length - 1] : lastYear;
  const rows = [];
  const refusals = {};
  const note = (r) => { if (r) refusals[r] = (refusals[r] || 0) + 1; };

  for (let Y = lo; Y <= hi; Y++) {
    const own = years[Y];
    const a = aphAsOf(years, Y);
    if (a.refused) { note(a.refused); continue; }
    const yieldY = own ? num(own.yield) : null;
    if (yieldY == null) { note(REPLAY_REFUSED.NO_YIELD); continue; }
    const px = prices && prices[Y];
    if (!px || num(px.projected) == null || num(px.harvest) == null) { note(REPLAY_REFUSED.NO_PRICE); continue; }

    const cellsByLevel = {};
    let anyPremium = false;
    for (const L of levels) {
      const prem = premiumFor ? premiumFor(L, Y, a.aph) : null;
      if (prem != null) anyPremium = true;
      const c = replayCell({ aph: a.aph, actualYield: yieldY, price: px, level: L, premiumPerAc: prem });
      if (c.refused) { note(c.refused); continue; }
      cellsByLevel[L.toFixed(2)] = c;
    }
    if (!Object.keys(cellsByLevel).length) continue;
    if (!anyPremium) note(REPLAY_REFUSED.NO_PREMIUM);
    rows.push({
      year: Y,
      aph: a.aph, aphYears: a.n, aphExact: a.exact,
      actualYield: yieldY,
      acres: own && num(own.acres) != null ? num(own.acres) : null,
      projected: num(px.projected), harvest: num(px.harvest),
      levels: cellsByLevel,
    });
  }
  return { rows, refusals };
}

// ── roll several databases up to one ledger ──────────────────────────────────
//
// ⚠ ACRE-WEIGHTED, AND A DATABASE WITH NO ACREAGE IS COUNTED AND NAMED.  A
// straight average over databases would let a 12-acre database outvote a
// 300-acre one, which is the blend-the-coverage-lines mistake this repo already
// records.  Where a year's acres are missing on every database the year is
// reported with an equal weighting and SAYS SO, because dropping it would
// shorten the record silently.
export function rollUp(perDatabase, levels) {
  const byYear = {};
  for (const { rows } of perDatabase) {
    for (const r of rows) {
      const b = (byYear[r.year] ||= { year: r.year, acres: 0, noAcres: 0, dbs: 0, levels: {}, aphAcc: 0, aphW: 0, yAcc: 0, exact: true, minYears: 99,
                                      projected: r.projected, harvest: r.harvest, priceSplit: false });
      // Databases in different states or practices can carry different prices in the
      // same year.  Rolling them into one price would state a number that applied to
      // neither, so the split is FLAGGED and the first is kept as the label.
      if (b.projected !== r.projected || b.harvest !== r.harvest) b.priceSplit = true;
      const w = r.acres != null && r.acres > 0 ? r.acres : null;
      if (w == null) b.noAcres++;
      b.dbs++;
      b.exact = b.exact && r.aphExact;
      b.minYears = Math.min(b.minYears, r.aphYears);
      const ww = w == null ? 1 : w;
      b.acres += w == null ? 0 : w;
      b.aphAcc += r.aph * ww; b.aphW += ww; b.yAcc += r.actualYield * ww;
      for (const L of levels) {
        const k = L.toFixed(2); const c = r.levels[k]; if (!c) continue;
        const t = (b.levels[k] ||= { indemC: 0, premC: 0, w: 0, priced: true, years: 0 });
        t.indemC += cents(c.indemnityPerAc) * ww;
        if (c.premiumPerAc == null) t.priced = false; else t.premC += cents(c.premiumPerAc) * ww;
        t.w += ww; t.years++;
      }
    }
  }
  const years = Object.values(byYear).sort((a, b) => a.year - b.year).map((b) => {
    const lv = {};
    for (const k of Object.keys(b.levels)) {
      const t = b.levels[k];
      const ind = dollars(t.indemC / t.w), prem = t.priced ? dollars(t.premC / t.w) : null;
      lv[k] = { indemnityPerAc: ind, premiumPerAc: prem, netPerAc: prem == null ? null : ind - prem, priced: t.priced };
    }
    return {
      year: b.year, databases: b.dbs, acres: b.acres || null, databasesWithoutAcres: b.noAcres,
      aph: Math.round((b.aphAcc / b.aphW) * 10) / 10,
      actualYield: Math.round((b.yAcc / b.aphW) * 10) / 10,
      aphExact: b.exact, aphMinYears: b.minYears === 99 ? null : b.minYears,
      projected: b.projected, harvest: b.harvest, priceSplit: b.priceSplit,
      levels: lv,
    };
  });

  // Per-level totals over the years the ledger actually covers.
  const totals = {};
  for (const L of levels) {
    const k = L.toFixed(2);
    let ind = 0, prem = 0, n = 0, lossYears = 0, priced = true;
    for (const y of years) {
      const c = y.levels[k]; if (!c) continue;
      n++; ind += c.indemnityPerAc; if (c.indemnityPerAc > 0) lossYears++;
      if (c.premiumPerAc == null) priced = false; else prem += c.premiumPerAc;
    }
    totals[k] = {
      level: L, years: n, lossYears,
      indemnityPerAc: n ? ind / n : null,
      premiumPerAc: priced && n ? prem / n : null,
      netPerAc: priced && n ? (ind - prem) / n : null,
      priced,
    };
  }
  return { years, totals };
}
